import { NgFor } from '@angular/common';
import { Component, EventEmitter, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ConfiguracoesAlimentadora, EntidadeConfiguracao, ExibicaoConfiguracao, ExibicoesConfiguracoes, TipoConfiguracaoConnect } from 'app/interfaces/configuracoes-connect';
import { filter, take } from 'rxjs';
import { DialogAgviewConnectService, ComunicadorService, ConfiguracoesService, ConfiguracoesSyncService } from 'app/services';
import { TranslocoModule } from '@jsverse/transloco';

@Component({
  selector: 'app-configuracao-alimentadora',
  standalone: true,
  imports: [NgFor, FormsModule, TranslocoModule],
  templateUrl: './configuracao-alimentadora.component.html',
  styleUrl: './configuracao-alimentadora.component.css'
})
export class ConfiguracaoAlimentadoraComponent {
  @Output() trocarTela = new EventEmitter<string>();

  public readonly ENTIDADE_CONFIGURACAO: EntidadeConfiguracao = 'FeederConfigEntity';
  public readonly CONFIG_COBRIDOR_ATIVACAO = 'covererControlActivated';
  public readonly CONFIG_COBRIDOR_AVANCO_RECUO = 'covererAdvanceRetreatTime';
  public readonly CONFIG_COBRIDOR_ATRASO_RECUO = 'covererDelayTimeToRetreat';
  private readonly CONFIG_RAMPAS = ['leftFeederControlActivated','rightFeederControlActivated']
  private readonly CONFIGS_SEMPRE_VISIVEIS = ['doubleRampControlActivaded', 'covererAdvanceRetreatTime', 'covererDelayTimeToRetreat']

  public itens: ExibicoesConfiguracoes = this.obterItensIniciais();
  public arrayItensFiltrados: ExibicoesConfiguracoes = this.itens;
  public configuracoesCadastradas!: TipoConfiguracaoConnect;
  public imei = '';

  constructor(
    private dialogService: DialogAgviewConnectService,
    private comunicadorService: ComunicadorService,
    private configuracoesService: ConfiguracoesService,
    private syncService: ConfiguracoesSyncService
  ) {}

  ngOnInit(): void {
    this.carregarConfiguracoes();
    this.atribuirValoresConfiguracoes();
    this.ouvirAtualizacaoAoSair();
    this.observarMudancasViaWebsocket();
  }

  private carregarConfiguracoes(): void {
    this.imei = this.configuracoesService.obterImei();
    this.configuracoesCadastradas = this.configuracoesService.obterPorEntidade('FeederConfigEntity');
  }

  private atribuirValoresConfiguracoes(): void {
    Object.entries(this.configuracoesCadastradas).forEach(([nome, valor]) => {
      const item = this.itens.find(i => i.nomeConfiguracao === nome);
      if (item) {
        item.valor = valor;
        this.mostrarItensOcultos(item);
      }
    });
  }

  public irParaTelaConfigGerais(): void {
    this.atualizarConfiguracoes();
    this.trocarTela.emit('configuracoes');
  }

  private ordenarObjeto(obj: Record<string, any>): Record<string, any> {
    return Object.keys(obj)
      .sort()
      .reduce((acc, key) => {
        acc[key] = obj[key];
        return acc;
      }, {} as Record<string, any>);
  }

  private formatarParaSalvar(itens: ExibicoesConfiguracoes): Record<string, any> {
    const configuracoes = itens
      .filter(item => item.deveSerMostrado || this.CONFIGS_SEMPRE_VISIVEIS.includes(item.nomeConfiguracao) )
      .reduce((acc, item) => ({ ...acc, [item.nomeConfiguracao]: item.valor }), {});
    return this.ordenarObjeto(configuracoes);
  }

  private ouvirAtualizacaoAoSair(): void {
    this.syncService.aoDispararAtualizacao()
      .pipe(take(1))
      .subscribe(() => this.atualizarConfiguracoes());
  }

  private atualizarConfiguracoes(): void {
    this.carregarConfiguracoes();

    const atuais: Record<string, any> = this.formatarParaSalvar(this.itens);
    const originais: Record<string, any> = this.ordenarObjeto(this.configuracoesCadastradas);

    const configuracoesComModo = {
      ...atuais,
      feederMode: this.definirModoAlimentadora(atuais),
      covererMode: originais['covererMode']
    };

    const atuaisOrdenadas = this.ordenarObjeto(configuracoesComModo);
    if (JSON.stringify(atuaisOrdenadas) !== JSON.stringify(originais)) {
      this.salvarConfiguracoes(atuaisOrdenadas);
      this.syncService.dispararNotificacaoEnvio();
    }
  }

  private salvarConfiguracoes(configuracoes: Record<string, any>): void {
    this.comunicadorService.atualizarConfiguracoes(this.imei, { [this.ENTIDADE_CONFIGURACAO]: configuracoes }).subscribe();
    this.configuracoesService.atualizarPorEntidade(this.ENTIDADE_CONFIGURACAO, configuracoes);
  }

  public abrirDialog(parametro: ExibicaoConfiguracao): void {
    const indice: number = this.itens.findIndex(item => item === parametro);
    if (indice === -1) return;

    this.dialogService.open(parametro).subscribe((novoValor: number) => {
      this.itens[indice].valor = novoValor;
    });
  }

  public filtrarConfiguracoes(texto: string): void {
    const textoLimpo = this.removerAcentos(texto).toLowerCase();
    this.arrayItensFiltrados = this.itens.filter(item => this.removerAcentos(item.titulo).toLowerCase().includes(textoLimpo));
  }

  private removerAcentos(texto: string): string {
    return texto.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  }


  protected verficarRampaDupla(): void {
    const rampaEsquerda = this.itens[13];
    const rampaDireita = this.itens[14];
    if(rampaDireita.valor && rampaEsquerda.valor) {
      this.itens[15].deveSerMostrado = true;
    }
    else {
      this.itens[15].deveSerMostrado = false
    }
  }

  public  mostrarItensOcultos(item: ExibicaoConfiguracao): void {
    if (this.CONFIG_RAMPAS.includes(item.nomeConfiguracao)) {
      this.verficarRampaDupla()
    };
    if (item.nomeConfiguracao !== this.CONFIG_COBRIDOR_ATIVACAO) return;

    this.exibirPorNome(this.CONFIG_COBRIDOR_AVANCO_RECUO, item.valor === true || item.valor === 1);
    this.exibirPorNome(this.CONFIG_COBRIDOR_ATRASO_RECUO, item.valor === true || item.valor === 1);
  }

  private exibirPorNome(nome: string, exibir: boolean): void {
    const item = this.arrayItensFiltrados.find(i => i.nomeConfiguracao === nome);
    if (item) {
      item.deveSerMostrado = exibir;
    }
  }

  private observarMudancasViaWebsocket(): void {
    this.configuracoesService.obterPorEntidadeWebsocket(this.ENTIDADE_CONFIGURACAO)
      .pipe(filter((config): config is ConfiguracoesAlimentadora => !!config))
      .subscribe(config => {
        this.configuracoesCadastradas = config;
        this.atribuirValoresConfiguracoes();
      });
  }

  private definirModoAlimentadora(configs: Record<string, any>): 'double' | 'rightOnly' | 'leftOnly' | 'disabled' | 'individual' {
    const right: boolean = Boolean(configs['rightFeederControlActivated']);
    const left: boolean = Boolean(configs['leftFeederControlActivated']);
    const doubleRamp: boolean = Boolean(configs['doubleRampControlActivaded']);
    if (right && left){
      this.itens[15].deveSerMostrado = true
      return 'double';
    }
    if (right) return 'rightOnly';
    if (left) return 'leftOnly';
    if (doubleRamp) {
      this.itens[15].deveSerMostrado = true
      return 'individual';
    } 
    return 'disabled';
  }

  private obterItensIniciais(): ExibicoesConfiguracoes {
    return [
      {
        nomeConfiguracao: 'maxLevelAllowedTreadmillHead',
        titulo: 'Nível Máx. Permitido - Cabeceira da Esteira',
        subtitulo: 'Configura o nível máx. permitido na cabeceira da esteira.',
        deveSerMostrado: true,
        isToggle: false,
        valor: 0,
        isSubconfiguracao: false
      },
      {
        nomeConfiguracao: 'maxLevelToTurnOffFeeder',
        titulo: 'Nível Máx. p/ Desligar a Alimentadora',
        subtitulo: 'Configura o nível máx. para desligar a alimentadora.',
        deveSerMostrado: true,
        isToggle: false,
        valor: 0,
        isSubconfiguracao: false
      },
      {
        nomeConfiguracao: 'minLevelToTurnOnFeeder',
        titulo: 'Nível Mín. p/ Ligar a Alimentadora',
        subtitulo: 'Configura o nível min.para ligar a alimentadora.',
        deveSerMostrado: true,
        isToggle: false,
        valor: 0,
        isSubconfiguracao: false
      },
      {
        nomeConfiguracao: 'feederAdvanceTime',
        titulo: 'Tempo de Avanço da Alimentadora',
        subtitulo: 'Configura o tempo de avanço da alimentadora.',
        deveSerMostrado: true,
        isToggle: false,
        valor: 0,
        unidadeMedida: 's',
        isSubconfiguracao: false
      },
      {
        nomeConfiguracao: 'feederRetreatTime',
        titulo: 'Tempo de Recuo da Alimentadora',
        subtitulo: 'Configura o tempo de recuo da alimentadora.',
        deveSerMostrado: true,
        isToggle: false,
        valor: 0,
        unidadeMedida: 's',
        isSubconfiguracao: false
      },
      {
        nomeConfiguracao: 'retreatTimeToFuel',
        titulo: 'Tempo de Recuo para Abastecimento',
        subtitulo: 'Configura o tempo de recuo para abastecimento.',
        deveSerMostrado: true,
        isToggle: false,
        unidadeMedida: 's',
        valor: 0,
        isSubconfiguracao: false
      },
      {
        nomeConfiguracao: 'minTensionBlockSensor',
        titulo: 'Sensor Bloco - Tensão Mínima',
        subtitulo: 'Configura a tensão mínima para o sensor bloco.',
        deveSerMostrado: true,
        isToggle: false,
        unidadeMedida: 'mV',
        valor: 0,
        isSubconfiguracao: false
      },
      {
        nomeConfiguracao: 'maxTensionBlockSensor',
        titulo: 'Sensor Bloco - Tensão Máxima',
        subtitulo: 'Configura a tensão máxima para o sensor bloco.',
        deveSerMostrado: true,
        isToggle: false,
        unidadeMedida: 'mV',
        valor: 0,
        isSubconfiguracao: false
      },
      {
        nomeConfiguracao: 'minPressureBlockSensor',
        titulo: 'Sensor Bloco - Pressão Mínima',
        subtitulo: 'Configura a pressão mínima para o sensor bloco.',
        deveSerMostrado: true,
        isToggle: false,
        valor: 0,
        unidadeMedida: 'bar',
        isSubconfiguracao: false
      },
      {
        nomeConfiguracao: 'maxPressureBlockSensor',
        titulo: 'Sensor Bloco - Pressão Máxima',
        subtitulo: 'Configura a pressão máxima para o sensor bloco.',
        deveSerMostrado: true,
        isToggle: false,
        valor: 0,
        unidadeMedida: 'bar',
        isSubconfiguracao: false
      },
      {
        nomeConfiguracao: 'covererControlActivated',
        titulo: 'Cobridor - Ativar/Desativar Controle',
        subtitulo: 'Ativa/Desativa controles do cobridor.',
        deveSerMostrado: true,
        isToggle: true,
        valor: false,
        isSubconfiguracao: false
      },
      {
        nomeConfiguracao: 'covererAdvanceRetreatTime',
        titulo: 'Cobridor - Tempo de Avanço/Recuo',
        subtitulo: 'Configura o tempo de avanço/recuo do cobridor.',
        deveSerMostrado: false,
        isToggle: false,
        unidadeMedida: 's',
        valor: 0,
        isSubconfiguracao: true
      },
      {
        nomeConfiguracao: 'covererDelayTimeToRetreat',
        titulo: 'Cobridor - Tempo de Atraso para Recuo',
        subtitulo: 'Configura o tempo de atraso para recuo.',
        deveSerMostrado: false,
        isToggle: false,
        unidadeMedida: 's',
        valor: 0,
        isSubconfiguracao: true
      },
      {
        nomeConfiguracao: 'leftFeederControlActivated',
        titulo: 'Ativar/Desativar Controle Esquerdo da Alimentadora',
        subtitulo: 'Ativa/Desativa o controle esquerdo da alimentadora.',
        deveSerMostrado: true,
        isToggle: true,
        valor: false,
        isSubconfiguracao: false
      },
      {
        nomeConfiguracao: 'rightFeederControlActivated',
        titulo: 'Ativar/Desativar Controle Direito da Alimentadora',
        subtitulo: 'Ativa/Desativa o controle direito da alimentadora.',
        deveSerMostrado: true,
        isToggle: true,
        valor: false,
        isSubconfiguracao: false
      },
      {
        nomeConfiguracao: 'doubleRampControlActivaded',
        titulo: 'Controle de Rampa Duplo',
        subtitulo: 'Ativa/Desativa o controle de rampa duplo.',
        deveSerMostrado: false,
        isToggle: true,
        valor: false,
        isSubconfiguracao: false
      }
    ];
  }
}
